import { ContactEntryVisibilityType } from '@commons/models/contact-entry';

import {
  AppointmentSkillDto,
  AppointmentTargetDto,
  AppointmentTargetType,
  Campaign,
  CampaignType,
  Profile,
  Skill,
  TimeSlot,
  TransferAppointmentRequestDto,
  UserDto,
} from '@contact/upgrade';

export interface IAppointmentDetailSkillDto {
  uuid: string;
  name: string;
}

/**
 * part of dto received when querying campaign represents a contact
 * @export
 * @interface IAppointmentContactDto
 */
export interface IAppointmentOptionnalContentDto {
  mail: string;
  language: string;
  bl_contact_uuid: string;
  bl_contact_id: string;
}

/**
 * part of dto received when querying campaign represents a contact
 * @export
 * @interface IAppointmentContactDto
 */
export interface IAppointmentContactDto {
  firstname: string;
  lastname: string;
  phone1: string;
  weight: number;
  campaignUUID: string;
  optionalContent?: IAppointmentOptionnalContentDto;
  csrUUID?: string;
}

/**
 * part of dto received when querying campaign represents a csr
 * @export
 * @interface IAppointmentCsr
 */
export interface IAppointmentCsr {
  uuid: string;
  firstName: string;
  lastName: string;
  phone1?: string;
  login?: string;
}

/**
 * part of dto received when querying campaign represents a csr
 * @export
 * @interface IAppointmentCsrDto
 */
export interface IAppointmentCsrDto {
  uuid: string;
  firstName: string;
  lastName: string;
  login?: string;
}

/**
 * part of dto send to campaign to create appointment : contains skill id and name
 * @export
 * @interface IAppointmentSkill
 */
export interface IAppointmentSkill {
  skillUuid: string;
  name: string;
}

/**
 * dto send to campaign to create a visio appointment
 * @export
 * @interface IAppointmentCampaignVisioDto
 */
export interface IAppointmentCampaignVisioDto {
  contact: IAppointmentContactDto;
  csr?: IAppointmentCsrDto;
  skillList: IAppointmentSkill[];
  awaitingParticipants?: IAppointmentCsrDto[];
  refusingParticipants?: IAppointmentCsrDto[];
  participantSkillList?: IAppointmentSkill[];
  participantTargetList?: IAppointmentTarget[];
  idCampaign: string;
  idTimeSlot: string;
  comment: string;
  targetList?: IAppointmentTarget[];
}

/**
 * dto send to campaign to create appointment
 * @export
 * @interface IAppointment
 */
export interface IAppointmentCampaignDto {
  idContact: string;
  idCsr: string;
  firstname: string;
  lastname: string;
  phone1: string;
  skills: IAppointmentSkill[];
  timeSlotUUID: string;
  campaignUUID: string;
  textMessage: string;
}

/**
 * dto send to BL to trace appointment
 * @export
 * @interface IAppointment
 */
export interface IAppointmentCampaignForBusinessDto {
  idContact: string;
  phoneNumber: string;
  skillList: string[];
  timeSlotId: string;
  timeSlotBeginDate: number;
  timeSlotEndDate: number;
  campaignId: string;
  origin: string;
  type: string;
}

/**
 * part of dto send to campaign to create appointment : contains campaign id
 * @export
 * @interface IAppointmentCampaign
 */
export interface IAppointmentCampaign {
  idCampaign: string;
}

/**
 * dto received when querying campaign represents an appointment
 * @export
 * @interface IAppointmentDto
 */
export interface IAppointmentDto {
  campaignType: string;
  campUuid: string;
  campName: string;
  firstname: string;
  lastname: string;
  phone1: string;
  idContact: string;
  timeSlotId: string;
  skillList: AppointmentSkillDto[];
  visibilityType: string;
}

/**
 * represents a type of target for the Csr or participants
 * @export
 * @interface IAppointmentTarget
 */
export interface IAppointmentTarget {
  uuid: string;
  name: string;
  type: AppointmentTargetType;
}

export enum AppointmentStatus {
  REQUESTED = 'REQUESTED',
  REQUEST_CANCELLED = 'REQUEST_CANCELLED',
  CONFIRMED = 'CONFIRMED',
  CANCELLED = 'CANCELLED',
  HONORED = 'HONORED',
  MISSED_BY_AGENT = 'MISSED_BY_AGENT',
  MISSED_BY_CONTACT = 'MISSED_BY_CONTACT',
  AWAITING = 'AWAITING',
  ACCEPTED = 'ACCEPTED',
}

export enum AppointmentCancelReason {
  CANCEL_BY_CSR = 'CANCEL_BY_CSR',
  CANCEL_BY_CONTACT = 'CANCEL_BY_CONTACT',
  NOT_SET = 'NOT_SET',
}

export interface IVisioAppointmentUserDto {
  uuid: string;
  login: string;
  firstName: string;
  lastName: string;
  lowerFirstName: string;
  lowerLastName: string;
}

export interface IVisioAppointmentUser {
  uuid: string;
  login: string;
  firstName: string;
  lastName: string;
}

export interface IVisioAppointmentGetDto {
  idAppointment: string;
  csr: IVisioAppointmentUserDto;
  skillList: IAppointmentDetailSkillDto[];
  comment: string;
}

export interface IVisioAppointmentDetailDto {
  uuid: string;
  idContact: string;
  contactFirstName: string;
  contactLastName: string;
  campaignUuid: string;
  csr: IVisioAppointmentUserDto;
  startDate: string;
  endDate: string;
  status: string;
  participants: IVisioAppointmentUserDto[];
  awaitingParticipants: IVisioAppointmentUserDto[];
  refusingParticipants: IVisioAppointmentUserDto[];
  skillList: IAppointmentDetailSkillDto[];
  participantSkillList: IAppointmentDetailSkillDto[];
  allSkills: IAppointmentDetailSkillDto[];
  comment: string;
  inviteStatus: string;
  reason: AppointmentCancelReason;
  recycled: boolean;
  modifications: IModificationLog[];
  type: string;
  flowUuid: string;
}

export interface IModificationLog {
  editor: string;
  modificationTime: Date;
}

export interface IVisioAppointmentDetailLight {
  appointmentId: string;
  csr: IVisioAppointmentUser;
  skillList?: Skill[];
  comment: string;
}

export interface IVisioAppointmentTransfer extends IVisioAppointmentDetailLight {
  targets?: IAppointmentTarget[];
}

export interface IVisioAppointmentDetail {
  appointmentId: string;
  contactId: string;
  csr: IVisioAppointmentUser;
  status: AppointmentStatus;
  startDate: Date;
  endDate: Date;
  idCampaign: string;
  participants: IVisioAppointmentUser[];
  awaitingParticipants: IVisioAppointmentUser[];
  refusingParticipants: IVisioAppointmentUser[];
  skillList: Skill[];
  comment: string;
  flowUuid: string;
  modifications: IModificationLog[];
}

/**
 * represents a contact appointment
 * @export
 * @interface IAppointment
 */
export interface IAppointment {
  /**
   * id of the contact in campaign
   * @type {string}
   * @memberof IAppointment
   */
  idContact: string;
  /**
   * id of the csr
   * @type {string}
   * @memberof IAppointment
   */
  idCsr: string;
  /**
   * login of the csr
   * @type {string}
   * @memberof IAppointment
   */
  loginCsr: string;
  /**
   * firstname of the csr
   * @type {string}
   * @memberof IAppointment
   */
  firstnameCsr?: string;
  /**
   * lastname of the csr
   * @type {string}
   * @memberof IAppointment
   */
  lastnameCsr?: string;
  /**
   * firstname of the contact
   * @type {string}
   * @memberof IAppointment
   */
  firstName: string;
  /**
   * lastname of the contact
   * @type {string}
   * @memberof IAppointment
   */
  lastName: string;
  /**
   * phone of the contact
   * @type {string}
   * @memberof IAppointment
   */
  phone: string;
  /**
   * skill list associated to this appointment
   * @type {ISkill[]}
   * @memberof IAppointment
   */
  skillList: Skill[];
  /**
   * timeslot of this appointment
   * @memberof IAppointment
   */
  timeSlot: TimeSlot;
  /**
   * campaign from which this appointment is associated
   * @memberof IAppointment
   */
  campaign: Campaign;
  /**
   * visibility type of this appointment after restrcition checking (server side)
   * @type {ContactEntryVisibilityType}
   * @memberof IAppointment
   */
  visibilityType: ContactEntryVisibilityType;
  /**
   * only used front side to know if this appointment has been qualified and closed
   * @type {boolean}
   * @memberof IAppointment
   */
  closed: boolean;
  /**
   * id of the customer card in contact
   */
  customerCardUuid: string;
  /**
   * external id of the contact
   */
  externalContactId: string;
  /**
   * type of the campaign, to differentiate visio and phone appointment
   */
  campaignType: string;

  /**
   * Comment
   */
  contextComment: string;

  /**
   * Optional content
   */
  optionalContent?: any;
}

export interface IVisioAppointment extends IAppointment {
  /**
   *  status
   */
  status: string;

  /**
   *  participants, should be changed to Iparticipant[] when middle is prepared
   */
  participants: string[];

  /**
   *  participants in awaiting lists, should be changed to Iparticipant[] when middle is prepared
   */
  awaitingParticipants: string[];

  /**
   *  participants in refusing lists, should be changed to Iparticipant[] when middle is prepared
   */
  refusingParticipants: string[];
  /**
   * Targets of the Visio appointment
   */
  targetList?: IAppointmentTarget[];

  /**
   * Comments
   */
  comment: string;
  /**
   * Comments Context
   */
  contextComment: string;
  /**
   * mail address of the contact
   * @type {string}
   * @memberof IVisioAppointment
   */
  email?: string;
  /**
   * language of the contact (for mail templates)
   * @type {string}
   * @memberof IVisioAppointment
   */
  language?: string;
  /**
   * the participant
   * @type {IAppointmentCsr}
   * @memberof IVisioAppointment
   */
  participant?: IAppointmentCsr;
  /**
   * skill list associated to the participant
   * @type {ISkill[]}
   * @memberof IVisioAppointment
   */
  participantSkillList?: Skill[];
  /**
   * target list associated to the participant
   * @type {IAppointmentTarget[]}
   * @memberof IVisioAppointment
   */
  participantTargetList?: IAppointmentTarget[];
  /**
   * the uuid of the selected customerCard
   */
  contactUuid: string;
  /**
   * the first name of the customercard
   */
  contactFirstName: string;
  /**
   * the last name of the customercard
   */
  contactLastName: string;
}

/**
 * represents a contact appointment
 * @class Appointment
 * @implements {IAppointment}
 */
export class Appointment implements IAppointment {
  idContact: string;
  idCsr: string;
  loginCsr: string;
  firstName: string;
  lastName: string;
  phone: string;
  skillList: Skill[] = [];
  campaign: Campaign;
  timeSlot: TimeSlot;
  visibilityType: ContactEntryVisibilityType;
  closed: boolean;
  customerCardUuid: string;
  externalContactId: string;
  campaignType: string;
  contextComment: string;

  static fromDto(toConvert: IAppointmentDto): IAppointment {
    return {
      idContact: toConvert.idContact,
      firstName: toConvert.firstname,
      lastName: toConvert.lastname,
      campaignType: toConvert.campaignType,
      skillList: toConvert.skillList.map(
        (skillToConvert: AppointmentSkillDto): Skill => ({
          id: skillToConvert.skillUuid,
          name: skillToConvert.name,
        })
      ),
      campaign: {
        id: toConvert.campUuid,
        name: toConvert.campName,
        type: CampaignType[toConvert.campaignType],
      },
      phone: toConvert.phone1,
      timeSlot: {
        id: toConvert.timeSlotId,
      },
      visibilityType: ContactEntryVisibilityType[toConvert.visibilityType],
      closed: false,
    } as IAppointment;
  }

  static toDto(toConvert: IAppointment): IAppointmentCampaignDto {
    return {
      idContact: toConvert.idContact,
      idCsr: toConvert.idCsr,
      firstname: toConvert.firstName,
      lastname: toConvert.lastName,
      textMessage: toConvert.contextComment,
      skills: toConvert.skillList.map(
        (skillToConvert: Skill): IAppointmentSkill => ({
          skillUuid: skillToConvert.id,
          name: skillToConvert.name,
        })
      ),
      campaignUUID: toConvert.campaign.id,
      phone1: toConvert.phone,
      timeSlotUUID: toConvert.timeSlot.id,
    } as IAppointmentCampaignDto;
  }

  static toDtoForTrace(toConvert: IAppointment, type: string, origin: string, skillListUuid: string[]): IAppointmentCampaignForBusinessDto {
    return {
      idContact: toConvert.idContact,
      skillList: skillListUuid.length > 0 ? skillListUuid : toConvert.skillList.map((skillToConvert: Skill) => skillToConvert.id),
      campaignId: toConvert.campaign.id,
      phoneNumber: toConvert.phone,
      timeSlotId: toConvert.timeSlot ? toConvert.timeSlot.id : '',
      timeSlotBeginDate: toConvert.timeSlot ? toConvert.timeSlot.beginDate : 0,
      timeSlotEndDate: toConvert.timeSlot ? toConvert.timeSlot.endDate : 0,
      type: type,
      origin: origin,
    } as IAppointmentCampaignForBusinessDto;
  }
}

export class VisioAppointmentDetail implements IVisioAppointmentDetail {
  appointmentId: string;
  comment: string;
  contactId: string;
  csr: IVisioAppointmentUser;
  endDate: Date;
  idCampaign: string;
  participants: IVisioAppointmentUser[];
  awaitingParticipants: IVisioAppointmentUser[];
  refusingParticipants: IVisioAppointmentUser[];
  skillList: Skill[];
  startDate: Date;
  status: AppointmentStatus;
  modifications: IModificationLog[];
  flowUuid: string;
  participant?: IAppointmentCsr;
  participantSkillList?: Skill[] = [];
  participantTargetList?: IAppointmentTarget[];
  targetList?: IAppointmentTarget[] = [];

  static fromDtoToLight(dto: IVisioAppointmentGetDto): IVisioAppointmentDetailLight {
    let ret: IVisioAppointmentDetailLight = {
      appointmentId: dto.idAppointment,
      csr: this.extractVisioAppointmentUserFromDto(dto.csr),
      comment: dto.comment,
    };
    if (!!dto.skillList) {
      ret.skillList = dto.skillList.map(
        (skillToConvert: IAppointmentDetailSkillDto): Skill => ({
          id: skillToConvert.uuid,
          name: skillToConvert.name,
        })
      );
    }
    return ret;
  }

  static fromDto(dto: IVisioAppointmentDetailDto): IVisioAppointmentDetail {
    dto.skillList = !!dto.skillList ? dto.skillList : [];
    dto.participants = !!dto.participants ? dto.participants : [];
    dto.awaitingParticipants = !!dto.awaitingParticipants ? dto.awaitingParticipants : [];
    dto.refusingParticipants = !!dto.refusingParticipants ? dto.refusingParticipants : [];
    let ret: IVisioAppointmentDetail = {
      appointmentId: dto.uuid,
      status: AppointmentStatus[dto.status],
      contactId: dto.idContact,
      csr: this.extractVisioAppointmentUserFromDto(dto.csr),
      startDate: new Date(dto.startDate),
      endDate: new Date(dto.endDate),
      idCampaign: dto.campaignUuid,
      comment: dto.comment,
      flowUuid: dto.flowUuid,
      skillList: dto.skillList.map(
        (skillToConvert: IAppointmentDetailSkillDto): Skill => ({
          id: skillToConvert.uuid,
          name: skillToConvert.name,
        })
      ),
      // TODO: may be changed when DTO decided
      participants: dto.participants.map((participant: IVisioAppointmentUserDto) => this.extractVisioAppointmentUserFromDto(participant)),
      awaitingParticipants: dto.awaitingParticipants.map((participant: IVisioAppointmentUserDto) =>
        this.extractVisioAppointmentUserFromDto(participant)
      ),
      refusingParticipants: dto.refusingParticipants.map((participant: IVisioAppointmentUserDto) =>
        this.extractVisioAppointmentUserFromDto(participant)
      ),
      modifications: this.sortModificationLog(dto.modifications),
    };

    return ret;
  }

  static extractVisioAppointmentUserFromDto(dto: IVisioAppointmentUserDto): IVisioAppointmentUser {
    return {
      uuid: dto ? dto.uuid : '',
      login: dto ? dto.login : '',
      firstName: dto ? dto.lowerFirstName : '',
      lastName: dto ? dto.lowerLastName : '',
    } as IVisioAppointmentUser;
  }

  static sortModificationLog(modfications: IModificationLog[]): IModificationLog[] {
    if (modfications && modfications.length > 0) {
      const comparator = (modifLog1: IModificationLog, modifLog2: IModificationLog) =>
        modifLog2.modificationTime.getTime() - modifLog1.modificationTime.getTime();
      return modfications.sort(comparator);
    }
    return [];
  }

  static toVisioCreationDto(toConvert: IVisioAppointment, currentCsr: Profile): IAppointmentCampaignVisioDto {
    let ret: IAppointmentCampaignVisioDto = {
      contact: {
        firstname: toConvert.contactFirstName,
        lastname: toConvert.contactLastName,
        phone1: toConvert.phone,
        weight: 1,
        campaignUUID: toConvert.campaign.id,
        optionalContent: {
          mail: toConvert.email || '',
          language: toConvert.language || '',
          bl_contact_uuid: toConvert.contactUuid || '',
          bl_contact_id: toConvert.externalContactId || '',
        },
      },
      // csr's skillList
      skillList: toConvert.skillList.map(
        (skillToConvert: Skill): IAppointmentSkill => ({
          skillUuid: skillToConvert.id,
          name: skillToConvert.name,
        })
      ),
      idCampaign: toConvert.campaign.id,
      idTimeSlot: toConvert.timeSlot.id,
      comment: toConvert.comment,
    };

    ret.csr = {
      uuid: currentCsr.id,
      login: currentCsr.login || '',
      firstName: currentCsr.firstname || '',
      lastName: currentCsr.lastname || '',
    };

    if (!!toConvert.idCsr) {
      ret.contact.csrUUID = toConvert.idCsr;
    }

    if (!!toConvert.participant) {
      ret.awaitingParticipants = [
        {
          uuid: toConvert.participant.uuid || '',
          login: toConvert.participant.login || '',
          firstName: toConvert.participant.firstName || '',
          lastName: toConvert.participant.lastName || '',
        },
      ];
    }
    if (!!toConvert.participantSkillList) {
      // participant's skillList
      ret.participantSkillList = toConvert.skillList.map(
        (skillToConvert: Skill): IAppointmentSkill => ({
          skillUuid: skillToConvert.id,
          name: skillToConvert.name,
        })
      );
    }
    if (!!toConvert.participantTargetList) {
      ret.participantTargetList = toConvert.participantTargetList;
    }
    if (!!toConvert.targetList) {
      ret.targetList = toConvert.targetList;
    }

    return ret;
  }

  static toVisioTransferDto(toConvert: IVisioAppointmentTransfer): TransferAppointmentRequestDto {
    // csr
    let csr: UserDto | undefined = undefined;
    if (!!toConvert.csr && !!toConvert.csr.uuid) {
      csr = {
        uuid: toConvert.csr.uuid,
        login: toConvert.csr.login || '',
        firstName: toConvert.csr.firstName,
        lastName: toConvert.csr.lastName,
      };
    }

    // csr 's target
    let targets: AppointmentTargetDto[] | undefined = undefined;
    if (!!toConvert.targets && toConvert.targets.length > 0) {
      targets = toConvert.targets.map(
        (targetToConvert: IAppointmentTarget): AppointmentTargetDto => ({
          name: targetToConvert.name,
          uuid: targetToConvert.uuid,
          type: targetToConvert.type,
        })
      );
    }

    const ret: TransferAppointmentRequestDto = {
      appointmentUuid: toConvert.appointmentId,
      skillList:
        (toConvert.skillList &&
          toConvert.skillList.map(
            (skillToConvert: Skill): AppointmentSkillDto => ({
              skillUuid: skillToConvert.id,
              name: skillToConvert.name,
            })
          )) ||
        [],
      comment: toConvert.comment,
      csr: csr,
      targets: targets,
    };

    return ret;
  }
}
